// Helpers PUROS do menu Escalas (sem React, sem rede): rótulos, validações que espelham a API,
// prévia do ciclo e agrupamento da cascata. A API (`schedule_routes.py`) continua sendo a autoridade;
// aqui só se evita mandar o que ela vai recusar e se explica o que ela devolve.
// Contrato: docs/escalas.md no aliado-recorder-api.

export const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
export const DIAS_CURTOS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

export const SCOPES = [
    { key: 'company', label: 'Empresa toda' },
    { key: 'unit', label: 'Unidade' },
    { key: 'store', label: 'Loja' },
    { key: 'salesman', label: 'Vendedor' },
];
export const SCOPE_LABEL = Object.fromEntries(SCOPES.map((s) => [s.key, s.label]));

export const KINDS = [
    { key: 'holiday', label: 'Feriado' },
    { key: 'closed', label: 'Loja fechada' },
    { key: 'day_off', label: 'Folga' },
    { key: 'absence', label: 'Falta' },
    { key: 'vacation', label: 'Férias' },
    { key: 'compensation', label: 'Reposição' },
    { key: 'extra_shift', label: 'Turno extra' },
];
export const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.key, k.label]));

export const EFFECTS = [
    { key: 'off', label: 'Folga (não trabalha)' },
    { key: 'work', label: 'Trabalha (horário da escala)' },
    { key: 'override_hours', label: 'Trabalha em horário próprio' },
];
export const EFFECT_LABEL = Object.fromEntries(EFFECTS.map((e) => [e.key, e.label]));

export const STATUSES = [
    { key: 'planned', label: 'Planejada' },
    { key: 'confirmed', label: 'Confirmada' },
];
export const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

export const SOURCE_LABEL = {
    pattern: 'Escala',
    national: 'Feriado nacional',
    store: 'Exceção da loja',
    salesman: 'Exceção do vendedor',
    none: 'Sem escala',
};

export const FUSOS = [
    'America/Sao_Paulo', 'America/Manaus', 'America/Fortaleza', 'America/Bahia', 'America/Belem',
    'America/Recife', 'America/Cuiaba', 'America/Campo_Grande', 'America/Rio_Branco', 'America/Noronha',
];

export const MAX_JANELA_DIAS = 62;

// --------------------------------------------------------------------------- congelamento do passado
// O dia anterior a `first_open_day` (que a API devolve nas listas e no /resolve) é "fechado": o que ele
// guarda só muda por REVISÃO, com motivo. Tudo aqui é tolerante a API antiga: sem `first_open_day` não
// há aviso de trava, e sem `freeze_state` o dia é tratado como aberto.
export const MOTIVO_MIN = 3;
export const motivoValido = (v) => String(v || '').trim().length >= MOTIVO_MIN;
export const MSG_MOTIVO = `Informe o motivo (pelo menos ${MOTIVO_MIN} caracteres)`;

/** `iso` é anterior ao primeiro dia aberto? Sem `primeiroAberto` (API antiga) nada é considerado fechado. */
export const diaFechado = (iso, primeiroAberto) => !!(primeiroAberto && iso && String(iso).split('T')[0] < primeiroAberto);

export const FREEZE_LABEL = {
    aberto: 'Dia aberto: calculado agora; a regra ainda pode mudar',
    congelado: 'Congelado',
    pendente: 'Fechado, ainda sem registro congelado (o fechamento grava em seguida)',
    nao_congelado: 'Histórico não congelado: calculado ao vivo agora',
};

export function estadoCongelamento(d) {
    return (d && d.freeze_state) || 'aberto';
}

/** Marcas visíveis de um dia: revisão (> 1 = alterado), divergência e estado de congelamento. */
export function marcasDia(d) {
    if (!d) return { alterado: false, revisao: null, divergente: false, estado: 'aberto' };
    const rev = d.revision != null ? Number(d.revision) : null;
    return {
        alterado: !!d.altered || (rev != null && rev > 1),
        revisao: rev,
        divergente: !!d.divergente,
        estado: estadoCongelamento(d),
    };
}

/** Classe do contorno do dia: tracejado = histórico não congelado, pontilhado = fechado e ainda sem registro. */
export function classeCongelamento(d) {
    const e = estadoCongelamento(d);
    if (e === 'nao_congelado') return 'esc-cong-nao';
    if (e === 'pendente') return 'esc-cong-pend';
    return '';
}

/** O que o padrão dizia SEM a exceção (`base` do /resolve), numa frase curta. */
export function descreverBase(b) {
    if (!b) return '—';
    if (b.expected === 'work') {
        const h = b.start && b.end ? ` ${b.start}–${b.end}${b.ends_next_day ? ' (+1 dia)' : ''}` : '';
        return `trabalha${h}`;
    }
    if (b.expected === 'off') return 'folga';
    return 'sem escala';
}

// --------------------------------------------------------------------------- datas
export function brDate(iso) {
    if (!iso) return '—';
    const p = String(iso).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

const pad = (n) => String(n).padStart(2, '0');

export function isoDeData(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function hojeISO() {
    return isoDeData(new Date());
}

/** Soma `n` dias a uma data ISO (YYYY-MM-DD), sem passar por fuso (meio-dia local evita virada). */
export function addDias(iso, n) {
    const [y, m, d] = String(iso).split('-').map(Number);
    const dt = new Date(y, m - 1, d, 12, 0, 0);
    dt.setDate(dt.getDate() + n);
    return isoDeData(dt);
}

export function diasEntre(a, b) {
    const [y1, m1, d1] = String(a).split('-').map(Number);
    const [y2, m2, d2] = String(b).split('-').map(Number);
    return Math.round((new Date(y2, m2 - 1, d2, 12) - new Date(y1, m1 - 1, d1, 12)) / 86400000);
}

/** 0 = segunda ... 6 = domingo, como o `weekday()` do Python que a API usa. */
export function diaDaSemana(iso) {
    const [y, m, d] = String(iso).split('-').map(Number);
    return (new Date(y, m - 1, d, 12).getDay() + 6) % 7;
}

export function primeiroDoMes(iso) { return `${String(iso).slice(0, 7)}-01`; }

export function ultimoDoMes(iso) {
    const [y, m] = String(iso).split('-').map(Number);
    return isoDeData(new Date(y, m, 0, 12));
}

export function erroJanela(de, ate, max = MAX_JANELA_DIAS) {
    if (!de || !ate) return 'Informe o início e o fim do período';
    if (ate < de) return 'O fim do período não pode ser anterior ao início';
    if (diasEntre(de, ate) + 1 > max) return `Período grande demais: no máximo ${max} dias por consulta`;
    return null;
}

// --------------------------------------------------------------------------- horários
export const horaValida = (v) => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(v || ''));

/** Turno que termina no dia seguinte: o fim é menor que o início (ex.: 19:00 → 07:00). */
export function viraMeiaNoite(inicio, fim) {
    return horaValida(inicio) && horaValida(fim) && fim < inicio;
}

export function resumoHorario(inicio, fim) {
    if (!inicio || !fim) return '';
    return `${inicio}–${fim}${viraMeiaNoite(inicio, fim) ? ' (+1 dia)' : ''}`;
}

// --------------------------------------------------------------------------- erros da API
/** Mensagem em português para mostrar: `detail` da API (string ou lista de validação) ou `error` do BFF. */
export function mensagemErro(e, padrao) {
    const d = e && e.response && e.response.data;
    if (d) {
        if (typeof d.detail === 'string' && d.detail) return d.detail;
        if (Array.isArray(d.detail) && d.detail.length) {
            return d.detail.map((x) => x.msg || String(x)).join('; ');
        }
        if (typeof d.error === 'string' && d.error) return d.error;
    }
    return padrao;
}

/** O BFF não é passthrough: rota esquecida cai no catch-all e devolve 200 com outro corpo. Trata como erro. */
export function exigirForma(r, chave) {
    if (!r || r.success !== true || (chave && !Array.isArray(r[chave]))) {
        throw new Error('resposta fora de forma');
    }
    return r;
}

// --------------------------------------------------------------------------- padrões
export function diasVazios(tipo, tamanho) {
    const n = tipo === 'weekly' ? 7 : tamanho;
    return Array.from({ length: n }, (_, i) => {
        const util = tipo === 'weekly' ? i < 5 : i < n - 1;
        return {
            day_index: i,
            is_working: util,
            start_time: util ? '09:00' : '',
            end_time: util ? '18:00' : '',
            break_minutes: util ? 60 : 0,
        };
    });
}

export function padraoVazio() {
    return {
        id: null,
        name: '',
        cycle_type: 'weekly',
        cycle_length: 7,
        works_on_holidays: false,
        active: true,
        days: diasVazios('weekly', 7),
        assignments_in_use: 0,
    };
}

/** Troca tipo ou tamanho do ciclo preservando os dias que continuam existindo. */
export function ajustarCiclo(form, tipo, tamanho) {
    const n = tipo === 'weekly' ? 7 : Math.max(2, Math.min(60, Number(tamanho) || 2));
    const base = diasVazios(tipo, n);
    const mesmoTipo = form.cycle_type === tipo;
    const days = base.map((d) => {
        const atual = mesmoTipo ? form.days.find((x) => x.day_index === d.day_index) : null;
        return atual ? { ...atual } : d;
    });
    return { ...form, cycle_type: tipo, cycle_length: n, days };
}

export function padraoParaForm(p) {
    return {
        id: p.id,
        name: p.name,
        cycle_type: p.cycle_type,
        cycle_length: p.cycle_length,
        works_on_holidays: !!p.works_on_holidays,
        active: !!p.active,
        assignments_in_use: p.assignments_in_use || 0,
        days: [...p.days]
            .sort((a, b) => a.day_index - b.day_index)
            .map((d) => ({
                day_index: d.day_index,
                is_working: !!d.is_working,
                start_time: d.start_time || '',
                end_time: d.end_time || '',
                break_minutes: d.break_minutes || 0,
            })),
    };
}

/** Corpo do /schedule/patterns/save. Dia de folga não leva horário nem intervalo. */
export function padraoParaPayload(form, subdomain) {
    return {
        id: form.id || undefined,
        subdomain,
        name: form.name.trim(),
        cycle_type: form.cycle_type,
        cycle_length: form.cycle_length,
        works_on_holidays: form.works_on_holidays,
        active: form.active,
        days: form.days.map((d) => ({
            day_index: d.day_index,
            is_working: d.is_working,
            start_time: d.is_working ? d.start_time : null,
            end_time: d.is_working ? d.end_time : null,
            break_minutes: d.is_working ? Number(d.break_minutes) || 0 : 0,
        })),
    };
}

// Mesmas regras de validate_pattern (schedule_resolver.py).
export function validarPadrao(f) {
    if (!f.name || !f.name.trim()) return 'O nome do padrão é obrigatório';
    if (f.cycle_type !== 'weekly' && f.cycle_type !== 'rotating') return 'Escolha o tipo de ciclo';
    if (f.cycle_type === 'weekly' && f.cycle_length !== 7) return 'Padrão semanal tem sempre 7 dias';
    if (f.cycle_type === 'rotating' && !(f.cycle_length >= 2 && f.cycle_length <= 60)) {
        return 'Padrão rotativo precisa de 2 a 60 dias de ciclo';
    }
    if (f.days.length !== f.cycle_length) return `O ciclo tem ${f.cycle_length} dias`;
    let trabalha = 0;
    for (const d of f.days) {
        const nome = f.cycle_type === 'weekly' ? DIAS_SEMANA[d.day_index] : `Dia ${d.day_index + 1}`;
        if (Number(d.break_minutes) < 0) return `${nome}: o intervalo não pode ser negativo`;
        if (!d.is_working) continue;
        trabalha += 1;
        if (!horaValida(d.start_time) || !horaValida(d.end_time)) return `${nome}: informe início e fim (HH:MM)`;
        if (d.start_time === d.end_time) return `${nome}: o início não pode ser igual ao fim`;
    }
    if (trabalha === 0) return 'O padrão não tem nenhum dia de trabalho';
    return null;
}

/** Dias da prévia: um item por dia do ciclo, com o texto do horário. */
export function previaPadrao(f) {
    return f.days.map((d) => ({
        rotulo: f.cycle_type === 'weekly' ? DIAS_CURTOS[d.day_index] : `Dia ${d.day_index + 1}`,
        trabalha: d.is_working,
        texto: d.is_working ? resumoHorario(d.start_time, d.end_time) : 'Folga',
        viraMeiaNoite: d.is_working && viraMeiaNoite(d.start_time, d.end_time),
    }));
}

export function resumoCiclo(f) {
    const t = f.days.filter((d) => d.is_working).length;
    const o = f.days.length - t;
    const txt = `${t} dia${t === 1 ? '' : 's'} de trabalho e ${o} de folga a cada ${f.cycle_length} dias`;
    return f.cycle_type === 'rotating' ? `${t}x${o}: ${txt}` : txt;
}

// --------------------------------------------------------------------------- atribuições
export function atribuicaoVazia() {
    return {
        id: null, scope: 'store', unit_id: '', store_id: '', salesman_id: '', pattern_id: '',
        valid_from: hojeISO(), valid_to: '', anchor_date: '', cycle_offset: 0, label: '', active: true,
        reason: '', has_closed_days: false, editable_fields: null,
    };
}

/** Form de edição a partir da linha da lista. Guarda o que a API disse sobre o que ainda pode mudar. */
export function atribuicaoParaForm(a) {
    return {
        id: a.id,
        scope: a.scope,
        unit_id: a.unit_id != null ? String(a.unit_id) : '',
        store_id: a.store_id != null ? String(a.store_id) : '',
        salesman_id: a.salesman_id != null ? String(a.salesman_id) : '',
        pattern_id: String(a.pattern_id),
        valid_from: String(a.valid_from || '').split('T')[0],
        valid_to: a.valid_to ? String(a.valid_to).split('T')[0] : '',
        anchor_date: a.anchor_date ? String(a.anchor_date).split('T')[0] : '',
        cycle_offset: a.cycle_offset || 0,
        label: a.label || '',
        active: !!a.active,
        reason: '',
        has_closed_days: !!a.has_closed_days,
        editable_fields: Array.isArray(a.editable_fields) ? a.editable_fields : null,
    };
}

/** Campo travado na edição? Só quando a API disse que há dia fechado e o campo não está em `editable_fields`. */
export function campoTravado(f, campo) {
    return !!(f && f.id && f.has_closed_days && !(f.editable_fields || ['label', 'valid_to']).includes(campo));
}

/** Atribuição nova com início no passado = carga de histórico (exige motivo e autor autorizado). */
export function cargaDeHistorico(f, primeiroAberto) {
    return !!(f && !f.id && diaFechado(f.valid_from, primeiroAberto));
}

export function validarAtribuicao(f, padrao, primeiroAberto) {
    if (f.scope === 'unit' && !f.unit_id) return 'Selecione a unidade';
    if (f.scope === 'store' && !f.store_id) return 'Selecione a loja';
    if (f.scope === 'salesman' && !f.salesman_id) return 'Selecione o vendedor';
    if (!f.pattern_id) return 'Selecione o padrão';
    if (!f.valid_from) return 'Informe a data de início';
    if (f.valid_to && f.valid_to < f.valid_from) return 'O fim da vigência não pode ser anterior ao início';
    if (cargaDeHistorico(f, primeiroAberto) && !motivoValido(f.reason)) {
        return `Carga de histórico: ${MSG_MOTIVO.toLowerCase()}`;
    }
    if (padrao && padrao.cycle_type === 'rotating') {
        if (!f.anchor_date) return 'Padrão rotativo exige a data âncora: o dia em que o ciclo está no dia 1';
        const off = Number(f.cycle_offset);
        if (!Number.isInteger(off) || off < 0 || off >= padrao.cycle_length) {
            return `O deslocamento do ciclo deve estar entre 0 e ${padrao.cycle_length - 1}`;
        }
    }
    return null;
}

export function atribuicaoParaPayload(f, subdomain, padrao, primeiroAberto) {
    const rot = padrao && padrao.cycle_type === 'rotating';
    return {
        ...(cargaDeHistorico(f, primeiroAberto) ? { reason: (f.reason || '').trim() } : {}),
        id: f.id || undefined,
        subdomain,
        scope: f.scope,
        // o alvo que não pertence ao escopo TEM de ir nulo: a API recusa campo sobrando
        unit_id: f.scope === 'unit' ? Number(f.unit_id) : null,
        store_id: f.scope === 'store' ? Number(f.store_id) : null,
        salesman_id: f.scope === 'salesman' ? Number(f.salesman_id) : null,
        pattern_id: Number(f.pattern_id),
        valid_from: f.valid_from,
        valid_to: f.valid_to || null,
        anchor_date: rot ? f.anchor_date : null,
        cycle_offset: rot ? Number(f.cycle_offset) || 0 : 0,
        label: (f.label || '').trim() || null,
        active: f.active,
    };
}

/**
 * Cascata: a partir do /schedule/resolve de um dia (uma linha por vendedor), agrupa por atribuição
 * vencedora. Responde "essa loja herda 5x2 da unit X": quantos vendedores usam cada atribuição.
 */
export function agruparHerancia(salesmen, assignments) {
    const porId = Object.fromEntries((assignments || []).map((a) => [a.id, a]));
    const grupos = new Map();
    (salesmen || []).forEach((s) => {
        const dia = (s.days || [])[0] || {};
        const chave = dia.assignment_id != null ? dia.assignment_id : 'nenhuma';
        if (!grupos.has(chave)) {
            const a = porId[chave];
            grupos.set(chave, {
                assignment_id: dia.assignment_id != null ? dia.assignment_id : null,
                pattern_name: dia.pattern_name || null,
                scope: a ? a.scope : null,
                valid_from: a ? a.valid_from : null,
                unit_id: a ? a.unit_id : null,
                nomes: [],
            });
        }
        grupos.get(chave).nomes.push(s.name || s.username || `Vendedor ${s.salesman_id}`);
    });
    return [...grupos.values()];
}

export function textoHerancia(g, nomeUnidade) {
    const n = g.nomes.length;
    const quem = `${n} vendedor${n === 1 ? '' : 'es'}`;
    if (g.assignment_id == null) return `${quem} sem escala cadastrada`;
    const onde = {
        company: 'da empresa',
        unit: nomeUnidade ? `da unidade ${nomeUnidade}` : 'da unidade',
        store: 'da loja',
        salesman: 'do próprio vendedor',
    }[g.scope] || '';
    return `${quem} usa${n === 1 ? '' : 'm'} «${g.pattern_name}» por atribuição ${onde} (desde ${brDate(g.valid_from)})`;
}

// --------------------------------------------------------------------------- exceções
export function excecaoVazia() {
    const hoje = hojeISO();
    return {
        id: null, level: 'salesman', store_id: '', salesman_ids: [], date_from: hoje, date_to: hoje,
        kind: 'absence', effect: 'off', start_time: '', end_time: '', status: 'confirmed', reason: '', active: true,
    };
}

export function validarExcecao(f) {
    if (f.level === 'store' && !f.store_id) return 'Selecione a loja';
    if (f.level === 'salesman' && (!f.salesman_ids || f.salesman_ids.length === 0)) {
        return 'Selecione ao menos um vendedor';
    }
    if (f.id && f.level === 'salesman' && f.salesman_ids.length !== 1) return 'Editar pede um único vendedor';
    if (!f.date_from || !f.date_to) return 'Informe o período';
    if (f.date_to < f.date_from) return 'O fim não pode ser anterior ao início';
    const temHora = !!(f.start_time || f.end_time);
    if (f.effect === 'off' && temHora) return 'Folga não tem horário: limpe início e fim';
    if (f.effect === 'override_hours' && !(horaValida(f.start_time) && horaValida(f.end_time))) {
        return 'Horário próprio exige início e fim (HH:MM)';
    }
    if (f.effect === 'work' && temHora && !(horaValida(f.start_time) && horaValida(f.end_time))) {
        return 'Informe início e fim juntos (HH:MM), ou nenhum dos dois';
    }
    if (f.start_time && f.start_time === f.end_time) return 'O início não pode ser igual ao fim';
    if (!motivoValido(f.reason)) return 'Informe o motivo da exceção (pelo menos 3 caracteres)';
    return null;
}

/** A exceção (o intervalo novo OU o original, na edição) toca dia fechado? Vira revisão do passado. */
export function excecaoTocaFechado(f, primeiroAberto) {
    if (!f || !primeiroAberto) return false;
    return diaFechado(f.date_from, primeiroAberto) || (!!f.id && diaFechado(f.date_from_original, primeiroAberto));
}

export function excecaoParaPayload(f, subdomain, dryRun) {
    const base = {
        id: f.id || undefined,
        subdomain,
        level: f.level,
        date_from: f.date_from,
        date_to: f.date_to,
        kind: f.kind,
        effect: f.effect,
        start_time: f.effect === 'off' ? null : f.start_time || null,
        end_time: f.effect === 'off' ? null : f.end_time || null,
        status: f.status,
        reason: (f.reason || '').trim() || null,
        active: f.active,
    };
    if (f.level === 'store') return { ...base, store_id: Number(f.store_id) };
    const ids = f.salesman_ids.map(Number);
    const alvo = ids.length === 1 ? { salesman_id: ids[0] } : { salesman_ids: ids };
    return { ...base, ...alvo, ...(dryRun ? { dry_run: true } : {}) };
}

export function excecaoParaForm(e) {
    return {
        id: e.id,
        level: e.level,
        store_id: e.store_id != null ? String(e.store_id) : '',
        salesman_ids: e.salesman_id != null ? [e.salesman_id] : [],
        date_from: String(e.date_from || '').split('T')[0],
        date_to: String(e.date_to || '').split('T')[0],
        date_from_original: String(e.date_from || '').split('T')[0],
        kind: e.kind,
        effect: e.effect,
        start_time: e.start_time || '',
        end_time: e.end_time || '',
        status: e.status || 'confirmed',
        reason: e.reason || '',
        active: !!e.active,
    };
}

// --------------------------------------------------------------------------- calendário
/** Classe CSS do dia pela origem do resultado. */
export function classeDia(d) {
    if (!d) return 'esc-dia-none';
    if (d.expected === 'unscheduled') return 'esc-dia-none';
    if (d.source === 'salesman') return d.expected === 'work' ? 'esc-dia-vend-work' : 'esc-dia-vend-off';
    if (d.source === 'store') return d.expected === 'work' ? 'esc-dia-loja-work' : 'esc-dia-loja-off';
    if (d.source === 'national') return 'esc-dia-nac';
    if (d.holiday && d.expected === 'work') return 'esc-dia-feriado-trabalha';
    return d.expected === 'work' ? 'esc-dia-work' : 'esc-dia-off';
}

export function tituloDia(d) {
    if (!d) return '';
    const h = d.start && d.end ? ` ${resumoHorario(d.start, d.end)}` : '';
    const linhas = [
        `${brDate(d.date)} (${d.weekday}) — ${d.label}${h}`,
        d.why,
        `Origem: ${SOURCE_LABEL[d.source] || d.source}`,
    ];
    // Com exceção por cima, mostra o que o padrão dizia: é a base de quem for editar ou tirar a exceção.
    if (d.base && d.source !== 'pattern' && d.source !== 'none') linhas.push(`Base (sem a exceção): ${descreverBase(d.base)}`);
    const m = marcasDia(d);
    if (m.estado === 'congelado') linhas.push(`Congelado${m.revisao != null ? ` (revisão ${m.revisao})` : ''}`);
    else if (m.estado !== 'aberto') linhas.push(FREEZE_LABEL[m.estado]);
    if (m.alterado) {
        linhas.push(`Alterado na revisão ${m.revisao != null ? m.revisao : '?'}: ${d.altered_reason || 'sem motivo registrado'}`);
    }
    if (m.divergente) linhas.push('ATENÇÃO: o recálculo ao vivo difere do congelado, sem explicação');
    return linhas.join('\n');
}

export function celulaDia(d) {
    if (!d) return '';
    if (d.expected === 'work') return d.start ? `${d.start}` : 'Trab.';
    if (d.expected === 'off') return d.kind ? d.label : 'Folga';
    return '—';
}
